import { useCallback } from 'react';

import { useUrlParam } from './useUrlParam';

type UseSortReturn<T = string> = {
  sort: SortState<T>;
  setSort: (name: T) => void;
};

const getNextDirection = (currentDirection: SortDirection): SortDirection => {
  if (currentDirection === 'asc') return 'desc';

  return currentDirection === 'desc' ? undefined : 'asc';
};

export const useSort = <T = string>(
  defaultValue?: SortState<T>,
  useUrl?: boolean
): UseSortReturn<T> => {
  const [sort, setSortState] = useUrlParam<SortState<T>>({
    name: 'sort',
    enable: useUrl ?? true,
    defaultValue: defaultValue ?? { name: undefined, direction: undefined },
  });

  const setSort = useCallback(
    (name: T) => {
      if (sort?.name !== name) {
        return setSortState({ name, direction: 'asc' });
      }

      const nextDirection = getNextDirection(sort.direction);
      return setSortState({
        name: nextDirection ? name : undefined,
        direction: nextDirection,
      });
    },
    [sort, setSortState]
  );

  return { sort: sort as SortState<T>, setSort };
};
